import { useMemo, useState, type FormEvent } from 'react';
import {
  ApiError,
  createClearance,
  fetchOrganizationClearanceTypes,
  updateClearance,
  type ClearanceCategory,
} from '../api';
import {
  correctionDraft,
  correctionRefusal,
  documentDateHint,
  emptyEntryDraft,
  entryDto,
  entryRefusal,
  isElectricalCategory,
  isoDay,
  MANUAL_BRIEFING_NOTE,
  renewalDraft,
  updateDto,
  withExpiresAt,
  withDocumentDate,
  withType,
  type ClearanceEntryDraft,
  type CorrectableClearance,
} from './clearance-entry';
import { localToday } from './personnel-rows';
import { useResource } from './useResource';

// Ввод, продление и исправление допуска в карточке работника (issue #524, #525).
//
// ОДНА ФОРМА НА ТРИ ДЕЙСТВИЯ. Продление — это ввод нового документа того же
// вида: прежняя запись не правится и остаётся в истории. Исправление — правка
// той же записи, и правила у неё те же, что при вводе, плюс обязательная
// причина. Поля во всех трёх случаях одни и те же, и разводить их по трём
// формам значило бы завести три набора правил к одному документу.
//
// ЧТО РАЗЛИЧАЕТСЯ:
//   ввод         — вид допуска выбирают, поля пустые;
//   продление    — вид выбран и не меняется, «кто выдал» из прежней записи,
//                  номер и дата пустые: продлевают НОВОЙ бумагой;
//   исправление  — вид и работник не меняются, поля заполнены текущими
//                  значениями записи, и добавлено поле «Причина исправления».
//
// ПРОВЕРКИ ЗДЕСЬ — ПОВТОРЕНИЕ СЕРВЕРНЫХ, А НЕ ВТОРЫЕ ПРАВИЛА. Настоящие живут
// в `apps/api/src/clearances/clearance-entry-rules.ts`, и последнее слово за
// ними: отказ сервера показывается рядом с формой ЕГО текстом, а не
// пересказывается своими словами.

/** Группы по электробезопасности так, как их принимает сервер: числом 2..5. */
const ELECTRICAL_GROUPS: { value: string; label: string }[] = [
  { value: '2', label: 'II' },
  { value: '3', label: 'III' },
  { value: '4', label: 'IV' },
  { value: '5', label: 'V' },
];

export type ClearanceEntryStart =
  | { mode: 'new' }
  /** Продление: вид допуска и «кто выдал» — из прежней записи. */
  | { mode: 'renew'; clearanceTypeId: string; issuedBy: string; typeName: string }
  /** Исправление: правится ЭТА запись, вид допуска и работник не меняются. */
  | { mode: 'correct'; clearanceId: string; typeName: string; record: CorrectableClearance };

export function ClearanceEntryForm({
  start,
  employeeNumber,
  workerName,
  maxAgeDays,
  token,
  organizationId,
  onSaved,
  onCancel,
  onUnauthorized,
}: {
  start: ClearanceEntryStart;
  employeeNumber: string;
  workerName: string;
  /** Насколько старым может быть документ в этой организации — от сервера. */
  maxAgeDays: number;
  token: string;
  organizationId: string;
  onSaved: () => void;
  onCancel: () => void;
  onUnauthorized: () => void;
}) {
  const types = useResource(
    () => fetchOrganizationClearanceTypes(token, organizationId),
    onUnauthorized,
    [token, organizationId],
  );
  const [draft, setDraft] = useState<ClearanceEntryDraft>(() => {
    if (start.mode === 'correct') return correctionDraft(start.record);
    return start.mode === 'renew' ? renewalDraft(start) : emptyEntryDraft();
  });
  const [reason, setReason] = useState('');
  const [serverRefusal, setServerRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const available = types.data ?? [];
  const selected = available.find((type) => type.id === draft.clearanceTypeId) ?? null;
  const today = useMemo(() => isoDay(localToday()), []);
  // Категория исправляемой записи берётся из НЕЁ САМОЙ, а не из справочника
  // действующих видов: вид могли отключить, а группа по ЭБ в документе от
  // этого не пропала, и поле для неё обязано остаться.
  const category: ClearanceCategory | undefined =
    start.mode === 'correct' ? start.record.category : selected?.category;
  const refusal =
    start.mode === 'correct'
      ? correctionRefusal(draft, category, maxAgeDays, today, reason, start.record.documentDate)
      : entryRefusal(draft, selected, maxAgeDays, today);
  const electrical = isElectricalCategory(category);
  // Продлевают вид, который организация у себя ОТКЛЮЧИЛА: запись есть, а вида
  // среди действующих уже нет. Выбрать другой в продлении нельзя, и «выберите
  // вид допуска» было бы советом, которому не последовать. Исправления это не
  // касается: правится существующая запись, и новый вид ей не нужен.
  const typeGone = start.mode === 'renew' && !types.loading && types.error === null && selected === null;

  const pickType = (id: string) =>
    setDraft((previous) => withType(previous, available.find((type) => type.id === id) ?? null));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (refusal !== null || busy) return;
    setBusy(true);
    setServerRefusal(null);
    try {
      if (start.mode === 'correct') {
        await updateClearance(start.clearanceId, updateDto(draft, category, reason), token, organizationId);
      } else {
        await createClearance(entryDto(draft, employeeNumber, selected), token, organizationId);
      }
      onSaved();
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 401) onUnauthorized();
      // Текст сервера — как есть. Он написан для того, кто вносит документ, и
      // пересказ своими словами отправил бы человека исправлять не то.
      setServerRefusal(error instanceof Error ? error.message : 'Запись не сохранилась.');
    } finally {
      setBusy(false);
    }
  };

  const title =
    start.mode === 'renew'
      ? `Продление: ${start.typeName}`
      : start.mode === 'correct'
        ? `Исправление: ${start.typeName}`
        : 'Новый допуск';

  return (
    <form className="clearance-entry" onSubmit={submit}>
      <h3 className="worker-card__title">{title}</h3>
      <p className="section__note">
        {workerName}, таб. № {employeeNumber}
      </p>

      {types.loading && types.data === null ? <p className="section__note">Загружаю виды допуска…</p> : null}
      {types.error !== null ? <p className="ui-error">{types.error}</p> : null}

      {start.mode === 'new' ? (
        <label className="clearance-entry__field">
          <span className="issue__label">Вид допуска</span>
          <select
            className="ui-input"
            value={draft.clearanceTypeId ?? ''}
            onChange={(event) => pickType(event.target.value)}
            disabled={busy}
          >
            <option value="">— выберите вид —</option>
            {available.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        // Вид допуска не меняется ни продлением, ни исправлением: продлевают
        // то, что было, а исправляют опечатку в документе. Сменить вид — это
        // другой документ, и вводят его заново.
        <p className="clearance-entry__fixed">Вид допуска: {start.typeName}</p>
      )}

      {/* Issue #543: первичный, повторный и внеплановый инструктаж проводит
          мастер в приложении, и допуск создаёт запись журнала. Ручной ввод
          инструктажа — только для проведённых до перехода на приложение. */}
      {start.mode !== 'correct' && category === 'BRIEFING' ? (
        <p className="section__note section__note--warn">{MANUAL_BRIEFING_NOTE}</p>
      ) : null}

      <label className="clearance-entry__field">
        <span className="issue__label">Номер документа-основания</span>
        <input
          className="ui-input"
          value={draft.documentNumber}
          onChange={(event) => setDraft((previous) => ({ ...previous, documentNumber: event.target.value }))}
          disabled={busy}
        />
      </label>

      <label className="clearance-entry__field">
        <span className="issue__label">Дата документа</span>
        <input
          className="ui-input"
          type="date"
          value={draft.documentDate}
          max={today}
          onChange={(event) => setDraft((previous) => withDocumentDate(previous, event.target.value, selected))}
          disabled={busy}
        />
        <span className="issue__hint">{documentDateHint(maxAgeDays)}</span>
      </label>

      <label className="clearance-entry__field">
        <span className="issue__label">Срок действия</span>
        <input
          className="ui-input"
          type="date"
          value={draft.expiresAt}
          onChange={(event) => setDraft((previous) => withExpiresAt(previous, event.target.value))}
          disabled={busy}
        />
        <span className="issue__hint">
          {start.mode === 'correct'
            ? 'Срок из самой записи — поправьте, если в документе другой.'
            : selected?.renewalPeriodDays
              ? 'Подставлен по периодичности вида — поправьте, если в документе другой.'
              : 'Пусто — допуск бессрочный.'}
        </span>
      </label>

      <label className="clearance-entry__field">
        <span className="issue__label">Кто выдал</span>
        <input
          className="ui-input"
          value={draft.issuedBy}
          onChange={(event) => setDraft((previous) => ({ ...previous, issuedBy: event.target.value }))}
          disabled={busy}
        />
      </label>

      {/* Группа по электробезопасности — только у допуска по ЭБ: у остальных
          видов ей нечего удостоверять, и сервер такую запись отвергает. */}
      {electrical ? (
        <label className="clearance-entry__field">
          <span className="issue__label">Группа по электробезопасности</span>
          <select
            className="ui-input"
            value={draft.electricalSafetyGroup}
            onChange={(event) =>
              setDraft((previous) => ({ ...previous, electricalSafetyGroup: event.target.value }))
            }
            disabled={busy}
          >
            <option value="">— не указана —</option>
            {ELECTRICAL_GROUPS.map((group) => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="clearance-entry__field">
        <span className="issue__label">Примечание</span>
        <textarea
          className="ui-input issue__area"
          value={draft.notes}
          onChange={(event) => setDraft((previous) => ({ ...previous, notes: event.target.value }))}
          disabled={busy}
        />
      </label>

      {/* Причина — только у исправления, и она обязательна: правится уже
          удостоверенный документ, и журнал обязан сказать, почему. */}
      {start.mode === 'correct' ? (
        <label className="clearance-entry__field">
          <span className="issue__label">Причина исправления</span>
          <textarea
            className="ui-input issue__area"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={busy}
          />
          <span className="issue__hint">Останется в истории допусков работника рядом с тем, что изменилось.</span>
        </label>
      ) : null}

      {serverRefusal !== null ? <p className="ui-error">{serverRefusal}</p> : null}
      {refusal !== null ? (
        <p className="section__note section__note--warn">
          {typeGone
            ? 'Этот вид допуска организация у себя отключила — новую запись по нему сервер не примет.'
            : refusal}
        </p>
      ) : null}

      <div className="clearance-entry__actions">
        <button type="submit" className="ui-btn ui-btn--primary" disabled={refusal !== null || busy}>
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
        <button type="button" className="ui-btn ui-btn--ghost" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  );
}
